import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeSlash, Spinner, EnvelopeSimple } from '@phosphor-icons/react';

import API_URL from '../utils/apiUrl';

export default function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resendStatus, setResendStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverifiedEmail(null);
    setResendStatus(null);
    setLoading(true);

    // Single field accepts either email or username — backend resolves either
    const result = await login(identifier, password);

    if (result.success) {
      const user = result.user;
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'parent') {
        navigate('/parent/dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      // Detect the structured email-not-verified error and show the resend flow
      if (result.detail && result.detail.code === 'email_not_verified') {
        setUnverifiedEmail(result.detail.email || identifier);
        setError(result.detail.message || 'Please verify your email address before signing in.');
      } else {
        setError(result.error);
      }
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: unverifiedEmail }),
      });
      if (res.status === 429) {
        setResendStatus('cooldown');
      } else if (res.ok) {
        setResendStatus('sent');
      } else {
        const body = await res.json().catch(() => ({}));
        setResendStatus(body?.detail || 'Could not resend — try again in a minute.');
      }
    } catch {
      setResendStatus('Network error — try again.');
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold mb-2">Welcome Back</h1>
          <p className="text-muted-foreground">Log in to continue your learning journey</p>
        </div>

        <div className="neo-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && !unverifiedEmail && (
              <div className="p-4 bg-destructive/10 border-2 border-destructive rounded-lg text-destructive text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            {unverifiedEmail && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500 rounded-lg text-sm space-y-3" data-testid="login-unverified-email">
                <div className="flex items-start gap-2">
                  <EnvelopeSimple size={20} weight="fill" className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-bold text-amber-900 dark:text-amber-200 mb-1">Please verify your email first.</p>
                    <p className="text-amber-900/90 dark:text-amber-200/90 leading-relaxed text-xs">
                      We sent a confirmation link to <strong>{unverifiedEmail}</strong>. Click it to activate your account, then sign in here.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="neo-btn-secondary w-full text-sm"
                  disabled={resendStatus === 'sending'}
                  data-testid="login-resend-verification-btn"
                >
                  {resendStatus === 'sending' ? 'Sending...' : 'Resend verification email'}
                </button>
                {resendStatus === 'sent' && (
                  <p className="text-xs text-emerald-700 dark:text-emerald-400 font-bold" data-testid="login-resend-sent">
                    Another link is on its way — check your inbox.
                  </p>
                )}
                {resendStatus === 'cooldown' && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 font-bold">
                    Please wait a minute before requesting another email.
                  </p>
                )}
                {typeof resendStatus === 'string' && !['sending', 'sent', 'cooldown'].includes(resendStatus) && (
                  <p className="text-xs text-destructive font-bold">{resendStatus}</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="identifier" className="block text-sm font-bold mb-2">
                Email or username
              </label>
              <input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="neo-input w-full"
                placeholder="you@example.com or alex_y10"
                required
                autoComplete="username"
                data-testid="login-email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="neo-input w-full pr-12"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="neo-btn w-full flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Spinner size={20} className="animate-spin" />
                  Logging in...
                </>
              ) : (
                'Log In'
              )}
            </button>

            <div className="text-right mt-2">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="mt-6 text-center text-sm">
            <p className="text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="font-bold text-primary hover:underline" data-testid="signup-link">
                Sign up free
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
